import React from 'react';

/**
 * The gap between two pages, where a document may end.
 *
 * EDITABLE (Document Studio, the default): a click toggles a cut, and an AI
 * suggestion carries a numeric 0–1 confidence shown as "AI 87%".
 *
 * READ-ONLY (split review): the PAGES are fixed — they never reorder and never
 * drag. Confidence is the proposal's 'high' | 'low', shown as the word;
 * Math.round('high' * 100) is NaN, which is what rendering a proposal through
 * the editable badge produced.
 *
 * READ-ONLY + cutsEditable: the pages stay fixed but the gap is a real button,
 * because the reviewer may move where documents begin — on a HELD batch (3c-2,
 * before its documents exist) and on an already-split one (3d, where moving a
 * cut records the correction and changes no document). Without it the gap must
 * not LOOK editable: no click handler, no pointer, no "Click to split" title.
 */
export function confidenceBadgeText(confidence) {
  if (typeof confidence === 'string') return confidence;
  if (typeof confidence === 'number' && Number.isFinite(confidence)) return `AI ${Math.round(confidence * 100)}%`;
  return null;
}

const SplitHandle = ({ beforePageIndex, isActive, isAISuggested, confidence, onToggle, readOnly = false, cutsEditable = false }) => {
  if (readOnly) {
    const label = confidenceBadgeText(confidence);
    // Boundary review of a HELD batch (slice 3c-2): the pages stay fixed, but the
    // reviewer may move where documents begin. The gap is then a real button.
    const editable = cutsEditable && typeof onToggle === 'function';
    const toggle = editable
      ? (e) => { e.stopPropagation(); onToggle(beforePageIndex); }
      : undefined;
    return (
      <div
        className={`ds-split-handle ds-split-handle--readonly ${isActive ? 'ds-split-handle--active' : ''} ${
          typeof confidence === 'string' ? `ds-split-handle--confidence-${confidence}` : ''
        } ${editable ? 'ds-split-handle--cuts-editable' : ''}`}
        role={editable ? 'button' : 'separator'}
        tabIndex={editable ? 0 : undefined}
        aria-pressed={editable ? isActive : undefined}
        onClick={toggle}
        onKeyDown={editable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(e); } } : undefined}
        aria-label={isActive ? `Cut before page ${beforePageIndex + 1}${label ? ` (${label} confidence)` : ''}` : `No cut before page ${beforePageIndex + 1}`}
        title={
          editable
            ? (isActive ? `Remove the cut before page ${beforePageIndex + 1}` : `Start a new document at page ${beforePageIndex + 1}`)
            : (isActive ? `The splitter started a new document at page ${beforePageIndex + 1}` : undefined)
        }
      >
        <div className="ds-split-handle-line" />
        {isActive && label && <div className="ds-split-handle-badge">{label}</div>}
        {isActive && <div className="ds-split-handle-icon">✂</div>}
      </div>
    );
  }

  const handleClick = (e) => {
    e.stopPropagation();
    onToggle(beforePageIndex);
  };

  return (
    <div
      className={`ds-split-handle ${isActive ? 'ds-split-handle--active' : ''} ${
        isAISuggested ? 'ds-split-handle--ai' : ''
      }`}
      onClick={handleClick}
      title={
        isAISuggested
          ? `AI suggested split (${Math.round(confidence * 100)}% confidence)`
          : 'Click to split document here'
      }
    >
      <div className="ds-split-handle-line" />
      {isAISuggested && (
        <div className="ds-split-handle-badge">
          {confidenceBadgeText(confidence)}
        </div>
      )}
      {isActive && (
        <div className="ds-split-handle-icon">✂</div>
      )}
    </div>
  );
};

export default SplitHandle;
